import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAIAssistant } from '../context/AIAssistantContext';
import { useLocation, useNavigate } from 'react-router-dom';
import AcumenAICircle from './admin/AcumenAICircle';
import { useLiveActivityFeed, ActivityMessage } from './AcuityActivityFeed';
import { Briefcase, Smile } from 'lucide-react';

// Onboarding Modal
function OnboardingModal({ onComplete }) {
  const { darkMode } = useTheme();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('Acuity');
  const [personality, setPersonality] = useState('friendly');
  const [voice, setVoice] = useState('nova');

  const voices = [
    { id: 'nova', name: 'Nova', description: 'Energetic, upbeat' },
    { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced' },
    { id: 'echo', name: 'Echo', description: 'Smooth, calm' },
    { id: 'onyx', name: 'Onyx', description: 'Deep, authoritative' },
    { id: 'shimmer', name: 'Shimmer', description: 'Bright, cheerful' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className={`w-full max-w-md rounded-2xl shadow-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} overflow-hidden`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-6 text-white">
          <div className="flex items-center gap-3">
            <AcumenAICircle size="lg" isProcessing={false} />
            <div>
              <h2 className="text-xl font-bold">Meet Your AI Assistant</h2>
              <p className="text-violet-200 text-sm">Let&apos;s personalize your experience</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>What would you like to name your assistant?</h3>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Robbie, Acuity, Max"
                className={`w-full px-4 py-3 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'} focus:ring-2 focus:ring-violet-500 focus:border-transparent`}
                maxLength={20}
              />
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>You can say &quot;Hey {name || 'Assistant'}&quot; to get help anytime!</p>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Choose a personality</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setPersonality('professional')}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    personality === 'professional'
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30'
                      : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      personality === 'professional' 
                        ? 'bg-violet-100 text-violet-600' 
                        : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Briefcase className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Professional</p>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Formal, task-focused, efficient</p>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setPersonality('friendly')}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    personality === 'friendly'
                      ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30'
                      : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      personality === 'friendly' 
                        ? 'bg-violet-100 text-violet-600' 
                        : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <Smile className="w-5 h-5" />
                    </div>
                    <div>
                      <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Friendly</p>
                      <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Warm, conversational, encouraging</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Select a voice</h3>
              <div className="grid grid-cols-2 gap-2">
                {voices.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => setVoice(v.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      voice === v.id
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30'
                        : darkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>{v.name}</p>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{v.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between`}>
          <button
            onClick={() => setStep(Math.max(1, step - 1))}
            className={`px-4 py-2 rounded-lg ${step === 1 ? 'invisible' : ''} ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            Back
          </button>
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`w-2 h-2 rounded-full ${s === step ? 'bg-violet-500' : darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
            ))}
          </div>
          <button
            onClick={() => {
              if (step < 3) setStep(step + 1);
              else onComplete(name || 'Acuity', personality, voice);
            }}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium"
          >
            {step === 3 ? 'Get Started' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Widget Component
export default function AIAssistantWidget() {
  const { darkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    isOpen, setIsOpen,
    isMinimized, setIsMinimized,
    settings,
    messages,
    isLoading,
    isSpeaking,
    isListening,
    pendingAction,
    showOnboarding,
    sendMessage,
    executeAction,
    startListening,
    stopListening,
    stopSpeaking,
    completeOnboarding,
    setPendingAction,
    clearHistory,
    updateSettings,
    isMuted,
    setIsMuted,
    volume,
    setVolume
  } = useAIAssistant();

  const [input, setInput] = useState('');
  const [fabPosition, setFabPosition] = useState({ x: null, y: null }); // null means use default position
  const [chatPosition, setChatPosition] = useState({ x: null, y: null }); // null means calculate from FAB position
  const [isFabDragging, setIsFabDragging] = useState(false);
  const [isChatDragging, setIsChatDragging] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [actionNotification, setActionNotification] = useState(null);
  const [wizardNotification, setWizardNotification] = useState(null);
  const [liveFeedEnabled, setLiveFeedEnabled] = useState(() => {
    const saved = localStorage.getItem('acuity_live_feed_enabled');
    return saved !== 'false'; // Default to true
  });
  const [resetPositionOnClose, setResetPositionOnClose] = useState(() => {
    const saved = localStorage.getItem('acuity_reset_position');
    return saved === 'true';
  });
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fabRef = useRef(null);
  const fabDragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 });
  const chatDragRef = useRef({ startX: 0, startY: 0, startPosX: 0, startPosY: 0 });

  // Live activity feed hook - pauses when Acuity is responding
  const handleActivitySendMessage = useCallback((msg) => {
    const currentPage = location.pathname.replace('/', '') || 'dashboard';
    sendMessage(msg, currentPage);
  }, [location.pathname, sendMessage]);

  const {
    activityMessages,
    handleActivityAction,
    dismissActivity,
    clearActivities
  } = useLiveActivityFeed(isLoading, liveFeedEnabled, handleActivitySendMessage);

  // Listen for wizard notifications
  useEffect(() => {
    const handleWizardNotification = (event) => {
      const { title, message, type, action } = event.detail;
      setWizardNotification({ title, message, type, action });
      
      // Also show a quick animation on the FAB
      if (!isOpen) {
        setActionNotification({ type: 'info', message: title });
      }
    };
    
    window.addEventListener('acuity-notification', handleWizardNotification);
    return () => window.removeEventListener('acuity-notification', handleWizardNotification);
  }, [isOpen]);
  
  // Clear wizard notification after 8 seconds
  useEffect(() => {
    if (wizardNotification) {
      const timer = setTimeout(() => setWizardNotification(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [wizardNotification]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Scroll to bottom when chat is opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      // Small delay to ensure the chat container is rendered
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      }, 100);
    }
  }, [isOpen, isMinimized]);

  // Auto-hide notification after 4 seconds
  useEffect(() => {
    if (actionNotification) {
      const timer = setTimeout(() => setActionNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [actionNotification]);

  // Keyboard shortcut: Cmd+Shift+A (Mac) or Ctrl+Shift+A (Windows/Linux) to toggle Acuity
  // Note: Cmd+K is now reserved for the global Command Palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+Shift+A (Mac) or Ctrl+Shift+A (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        e.stopPropagation();
        
        if (isOpen) {
          // If open, close it
          setIsOpen(false);
        } else {
          // If closed, open it
          setIsOpen(true);
          setIsMinimized(false);
          // Focus the input after opening
          setTimeout(() => {
            const input = document.querySelector('[data-testid="acuity-input"]');
            if (input) input.focus();
          }, 100);
        }
      }
      
      // Escape key to close
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, setIsOpen, setIsMinimized]);

  // Handle send message
  const handleSend = () => {
    if (!input.trim()) return;
    const currentPage = location.pathname.replace('/', '') || 'dashboard';
    sendMessage(input, currentPage);
    setInput('');
  };

  // Handle action confirmation
  const handleActionConfirm = async () => {
    if (pendingAction) {
      const result = await executeAction(pendingAction, true);
      
      if (result.success) {
        // Handle navigation
        if (result.navigate) {
          navigate(result.navigate);
          setActionNotification({ type: 'success', message: `Navigating to ${result.navigate}` });
        } 
        // Handle draft email - show it in the chat
        else if (result.draft) {
          setActionNotification({ type: 'success', message: 'Email draft created! Check below.' });
        }
        // Handle other successful actions
        else if (result.result) {
          const actionType = result.result.type;
          const actionMessages = {
            'reminder': 'Reminder set successfully!',
            'calendar_event': 'Calendar event created!',
            'message': 'Message sent successfully!',
            'solicitation': 'Solicitation created!',
            'vendor': 'Vendor record created!'
          };
          setActionNotification({ 
            type: 'success', 
            message: actionMessages[actionType] || 'Action completed successfully!' 
          });
        }
      } else {
        setActionNotification({ 
          type: 'error', 
          message: result.error || 'Action failed. Please try again.' 
        });
      }
    }
  };

  // Dragging handlers for FAB button
  const handleFabMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return;
    // Don't preventDefault - allow click to propagate
    setIsFabDragging(true);
    setHasDragged(false);
    
    // If fabPosition is null, calculate current position from element
    const rect = e.currentTarget.getBoundingClientRect();
    const currentX = fabPosition.x !== null ? fabPosition.x : rect.left;
    const currentY = fabPosition.y !== null ? fabPosition.y : rect.top;
    
    fabDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: currentX,
      startPosY: currentY
    };
  };

  const handleFabMouseMove = (e) => {
    if (!isFabDragging) return;
    const dx = e.clientX - fabDragRef.current.startX;
    const dy = e.clientY - fabDragRef.current.startY;
    
    // Only count as drag if moved more than 5 pixels
    if (Math.abs(dx) > 5 || Math.abs(dy) > 5) {
      setHasDragged(true);
    }
    
    // Constrain to viewport bounds (FAB is 44px - w-11)
    const newX = Math.max(0, Math.min(window.innerWidth - 44, fabDragRef.current.startPosX + dx));
    const newY = Math.max(0, Math.min(window.innerHeight - 44, fabDragRef.current.startPosY + dy));
    
    setFabPosition({ x: newX, y: newY });
  };

  const handleFabMouseUp = () => {
    setIsFabDragging(false);
  };

  // Dragging handlers for Chat window
  const handleChatMouseDown = (e) => {
    if (e.target.closest('.no-drag')) return;
    e.preventDefault();
    setIsChatDragging(true);
    
    // Get current position from element if chatPosition is null
    const rect = e.currentTarget.closest('.fixed').getBoundingClientRect();
    const currentX = chatPosition.x !== null ? chatPosition.x : rect.left;
    const currentY = chatPosition.y !== null ? chatPosition.y : rect.top;
    
    chatDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: currentX,
      startPosY: currentY
    };
  };

  const handleChatMouseMove = (e) => {
    if (!isChatDragging) return;
    const dx = e.clientX - chatDragRef.current.startX;
    const dy = e.clientY - chatDragRef.current.startY;
    
    // Constrain to viewport bounds
    const chatWidth = 384;
    const chatHeight = 512;
    const newX = Math.max(0, Math.min(window.innerWidth - chatWidth, chatDragRef.current.startPosX + dx));
    const newY = Math.max(0, Math.min(window.innerHeight - chatHeight, chatDragRef.current.startPosY + dy));
    
    setChatPosition({ x: newX, y: newY });
  };

  const handleChatMouseUp = () => {
    setIsChatDragging(false);
  };

  // FAB drag effect
  useEffect(() => {
    if (isFabDragging) {
      window.addEventListener('mousemove', handleFabMouseMove);
      window.addEventListener('mouseup', handleFabMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleFabMouseMove);
      window.removeEventListener('mouseup', handleFabMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFabDragging]);

  // Chat drag effect
  useEffect(() => {
    if (isChatDragging) {
      window.addEventListener('mousemove', handleChatMouseMove);
      window.addEventListener('mouseup', handleChatMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleChatMouseMove);
      window.removeEventListener('mouseup', handleChatMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChatDragging]);

  // Don't render on login/register pages
  if (['/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname)) {
    return null;
  }

  const assistantName = settings?.assistant_name || 'Acuity';

  return (
    <>
      {/* Onboarding Modal */}
      {showOnboarding && <OnboardingModal onComplete={completeOnboarding} />}

      {/* Wizard Notification Toast */}
      {wizardNotification && (
        <div 
          className="fixed top-20 right-6 z-[60] max-w-sm animate-fade-in"
        >
          <div className={`p-4 rounded-xl shadow-2xl border backdrop-blur-sm ${
            darkMode 
              ? 'bg-violet-900/90 border-violet-700 text-white' 
              : 'bg-violet-50 border-violet-200'
          }`}>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-violet-600 rounded-full flex items-center justify-center">
                <span className="text-white text-lg"></span>
              </div>
              <div className="flex-1">
                <h4 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-violet-900'}`}>
                  {wizardNotification.title}
                </h4>
                <p className={`text-sm mt-1 ${darkMode ? 'text-violet-200' : 'text-violet-700'}`}>
                  {wizardNotification.message}
                </p>
                {wizardNotification.action && (
                  <button
                    onClick={() => {
                      navigate(wizardNotification.action.path);
                      setWizardNotification(null);
                    }}
                    className="mt-2 px-3 py-1 text-xs font-medium bg-violet-600 text-white rounded-full hover:bg-violet-700 transition-colors"
                  >
                    {wizardNotification.action.label}
                  </button>
                )}
              </div>
              <button 
                onClick={() => setWizardNotification(null)}
                className={`flex-shrink-0 p-1 rounded-full hover:bg-black/10 ${darkMode ? 'text-violet-300' : 'text-violet-500'}`}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Notification Toast */}
      {actionNotification && (
        <div 
          className={`fixed bottom-40 right-6 z-[60] px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in ${
            actionNotification.type === 'success' 
              ? 'bg-green-600 text-white' 
              : actionNotification.type === 'info'
              ? 'bg-violet-600 text-white'
              : 'bg-red-600 text-white'
          }`}
        >
          {actionNotification.type === 'success' ? (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ) : actionNotification.type === 'info' ? (
            <span></span>
          ) : (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className="text-sm font-medium">{actionNotification.message}</span>
          <button 
            onClick={() => setActionNotification(null)}
            className="ml-2 hover:opacity-80"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Floating Button - Draggable */}
      {!isOpen && (
        <div
          className="fixed z-[100] select-none group"
          style={fabPosition.x !== null ? {
            left: `${fabPosition.x}px`,
            top: `${fabPosition.y}px`,
          } : {
            // Default position: centered horizontally in top bar
            left: '50%',
            transform: 'translateX(-50%)',
            top: '12px',
          }}
        >
          {/* Keyboard shortcut tooltip */}
          <div className={`absolute -bottom-8 left-1/2 -translate-x-1/2 px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none ${
            darkMode ? 'bg-gray-900 text-gray-300' : 'bg-gray-800 text-gray-200'
          }`}>
            {navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K'}
          </div>
          <div
            ref={fabRef}
            onMouseDown={handleFabMouseDown}
            onClick={() => {
              // Only open if we didn't drag (prevents opening when user just wants to move)
              if (!hasDragged) {
                // Calculate chat position based on FAB position
                const chatWidth = 384; // w-96 = 24rem = 384px
                const chatHeight = 512; // h-[32rem] = 512px
                const fabSize = 44; // w-11 = 2.75rem = 44px
                const margin = 10; // Margin from viewport edges
                
                let newChatX, newChatY;
                
                if (fabPosition.x !== null) {
                  // FAB has been dragged - position chat relative to FAB
                  // Default: try to position chat so FAB is at bottom-right
                  newChatX = fabPosition.x + fabSize - chatWidth;
                  newChatY = fabPosition.y + fabSize - chatHeight;
                  
                  // If chat would go off-screen left, position to the right of FAB
                  if (newChatX < margin) {
                    newChatX = Math.min(fabPosition.x, window.innerWidth - chatWidth - margin);
                  }
                  
                  // If chat would go off-screen top, position below FAB
                  if (newChatY < margin) {
                    newChatY = Math.min(fabPosition.y, window.innerHeight - chatHeight - margin);
                  }
                  
                  // Final bounds check - ensure chat is fully visible
                  newChatX = Math.max(margin, Math.min(newChatX, window.innerWidth - chatWidth - margin));
                  newChatY = Math.max(margin, Math.min(newChatY, window.innerHeight - chatHeight - margin));
                } else {
                  // FAB at default position (center top) - open chat below FAB, centered
                  newChatX = (window.innerWidth - chatWidth) / 2; // Center horizontally
                  newChatY = 60; // Just below the top bar
                }
                
                setChatPosition({ x: newChatX, y: newChatY });
                setIsOpen(true);
              }
            }}
            className="w-11 h-11 rounded-full bg-gradient-to-r from-violet-400/60 to-indigo-400/60 text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group"
            style={{ cursor: isFabDragging ? 'grabbing' : 'grab' }}
            title={`Talk to ${assistantName} (drag to move)`}
          >
            <AcumenAICircle size="md" isProcessing={false} />
          </div>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`fixed z-50 transition-all duration-200 ${
            isMinimized ? 'w-72 h-14' : 'w-96 h-[32rem]'
          } ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl overflow-hidden flex flex-col`}
          style={{
            left: chatPosition.x !== null ? `${chatPosition.x}px` : undefined,
            top: chatPosition.y !== null ? `${chatPosition.y}px` : undefined,
            right: chatPosition.x === null ? '1.5rem' : undefined,
            bottom: chatPosition.y === null ? '6rem' : undefined,
            cursor: isChatDragging ? 'grabbing' : 'auto'
          }}
        >
          {/* Header - Draggable */}
          <div
            className="bg-gradient-to-r from-violet-600 to-indigo-600 p-3 flex items-center justify-between cursor-grab active:cursor-grabbing"
            onMouseDown={handleChatMouseDown}
          >
            <div className="flex items-center gap-2">
              <AcumenAICircle size="sm" isProcessing={isLoading || isSpeaking || isListening} />
              <div>
                <h3 className="text-white font-semibold text-sm">{assistantName}</h3>
                <p className="text-violet-200 text-xs flex items-center gap-1.5">
                  <span>{isLoading ? 'Thinking...' : isSpeaking ? 'Speaking...' : isListening ? 'Listening...' : 'Online'}</span>
                  <span className="opacity-60">•</span>
                  <span className="opacity-60 text-[10px]">{navigator.platform.includes('Mac') ? '⌘K' : 'Ctrl+K'}</span>
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 no-drag">
              {/* Prominent Mute Button */}
              <button
                onClick={() => setIsMuted(!isMuted)}
                className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-all ${
                  isMuted 
                    ? 'bg-red-500/80 text-white hover:bg-red-500' 
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
                title={isMuted ? 'Click to unmute' : 'Click to mute'}
              >
                {isMuted ? (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                    <span>Muted</span>
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                    </svg>
                    <span>Mute</span>
                  </>
                )}
              </button>

              {/* Volume Control */}
              <div className="relative">
                <button
                  onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                  className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center text-white"
                  title="Volume Settings"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  </svg>
                </button>
                {/* Volume Dropdown */}
                {showVolumeSlider && (
                  <>
                    {/* Click outside to close */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowVolumeSlider(false)}
                    />
                    <div className="absolute top-full right-0 mt-1 p-3 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 min-w-[160px]">
                      {/* Volume Slider */}
                      <div>
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">
                          Volume: {Math.round(volume * 100)}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                        value={volume}
                        onChange={(e) => {
                          setVolume(parseFloat(e.target.value));
                          if (isMuted && parseFloat(e.target.value) > 0) setIsMuted(false);
                        }}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-violet-600"
                      />
                    </div>
                  </div>
                  </>
                )}
              </div>
              <button
                onClick={() => setShowSettings(true)}
                className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center text-white"
                title="Settings"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center text-white"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMinimized ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
                </svg>
              </button>
              <button
                onClick={() => {
                  if (resetPositionOnClose) {
                    setFabPosition({ x: null, y: null });
                  }
                  setIsOpen(false);
                }}
                className="w-7 h-7 rounded-full hover:bg-white/20 flex items-center justify-center text-white"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Chat Area */}
          {!isMinimized && (
            <>
              {/* Messages - includes both chat and activity */}
              <div
                ref={chatContainerRef}
                className={`flex-1 overflow-y-auto p-4 space-y-3 ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}
              >
                {messages.length === 0 && activityMessages.length === 0 && (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 flex items-center justify-center">
                      <svg className="w-8 h-8 text-violet-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                      </svg>
                    </div>
                    <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>Hi! I&apos;m {assistantName}</p>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>How can I help you today?</p>
                    {liveFeedEnabled && (
                      <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        I&apos;ll keep you updated on important activity
                      </p>
                    )}
                  </div>
                )}

                {/* Merge and sort messages and activities by timestamp */}
                {[...messages, ...(liveFeedEnabled ? activityMessages : [])]
                  .sort((a, b) => a.timestamp - b.timestamp)
                  .map((item) => (
                    item.role === 'activity' ? (
                      <ActivityMessage
                        key={item.id}
                        activity={item}
                        onAction={handleActivityAction}
                        onDismiss={dismissActivity}
                        darkMode={darkMode}
                        assistantName={assistantName}
                      />
                    ) : (
                      <div key={item.id} className={`flex ${item.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                          item.role === 'user'
                            ? 'bg-violet-600 text-white rounded-br-md'
                            : darkMode ? 'bg-gray-700 text-gray-100 rounded-bl-md' : 'bg-white text-gray-800 rounded-bl-md shadow'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{item.content}</p>
                          <p className={`text-xs mt-1 ${item.role === 'user' ? 'text-violet-200' : darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    )
                  ))}

                {isLoading && (
                  <div className="flex justify-start">
                    <div className={`rounded-2xl px-4 py-3 ${darkMode ? 'bg-gray-700' : 'bg-white shadow'}`}>
                      <div className="flex items-center gap-2">
                        <AcumenAICircle size="sm" isProcessing={true} />
                        <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Pending Action */}
              {pendingAction && (
                <div className={`px-4 py-3 border-t ${darkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
                  <p className={`text-xs mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Confirm action:</p>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm flex-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                      {pendingAction.type?.replace('_', ' ')}
                    </span>
                    <button
                      onClick={handleActionConfirm}
                      className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setPendingAction(null)}
                      className={`px-3 py-1 text-sm rounded-lg ${darkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className={`p-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                {/* Clear Chat Button - only show when there are messages */}
                {messages.length > 0 && (
                  <div className="flex justify-center mb-2">
                    <button
                      onClick={() => {
                        if (window.confirm('Clear all chat history?')) {
                          clearHistory();
                        }
                      }}
                      className={`px-3 py-1 text-xs rounded-full flex items-center gap-1 transition-all ${
                        darkMode 
                          ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                      title="Clear chat history"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Clear Chat
                    </button>
                  </div>
                )}
                <form 
                  data-testid="acuity-input-form"
                  onSubmit={(e) => { e.preventDefault(); handleSend(); }}
                  className="flex items-center gap-2"
                >
                  {/* Voice Button */}
                  <button
                    type="button"
                    onClick={isListening ? stopListening : startListening}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                      isListening
                        ? 'bg-red-500 text-white animate-pulse'
                        : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    title={isListening ? 'Stop listening' : 'Voice input'}
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </button>

                  {/* Text Input */}
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={`Ask ${assistantName}...`}
                    className={`flex-1 px-4 py-2 rounded-full border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-gray-100 border-gray-200 text-gray-800 placeholder-gray-500'} focus:outline-none focus:ring-2 focus:ring-violet-500`}
                    disabled={isLoading}
                  />

                  {/* Send Button */}
                  <button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="w-10 h-10 rounded-full bg-violet-600 text-white flex items-center justify-center hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </button>

                  {/* Stop Speaking */}
                  {isSpeaking && (
                    <button
                      type="button"
                      onClick={stopSpeaking}
                      className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-600"
                      title="Stop speaking"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                      </svg>
                    </button>
                  )}
                </form>
              </div>
            </>
          )}
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <SettingsModal
          settings={settings}
          onClose={() => setShowSettings(false)}
          onSave={updateSettings}
          onClearHistory={() => {
            clearHistory();
            clearActivities();
          }}
          resetPositionOnClose={resetPositionOnClose}
          setResetPositionOnClose={(val) => {
            setResetPositionOnClose(val);
            localStorage.setItem('acuity_reset_position', val.toString());
          }}
          liveFeedEnabled={liveFeedEnabled}
          setLiveFeedEnabled={(val) => {
            setLiveFeedEnabled(val);
            localStorage.setItem('acuity_live_feed_enabled', val.toString());
          }}
        />
      )}
    </>
  );
}

// Settings Modal Component
function SettingsModal({ settings, onClose, onSave, onClearHistory, resetPositionOnClose, setResetPositionOnClose, liveFeedEnabled, setLiveFeedEnabled }) {
  const { darkMode } = useTheme();
  const [name, setName] = useState(settings?.assistant_name || 'Acuity');
  const [personality, setPersonality] = useState(settings?.personality || 'friendly');
  const [voice, setVoice] = useState(settings?.voice || 'nova');
  const [voiceEnabled, setVoiceEnabled] = useState(settings?.voice_enabled ?? true);
  const [voiceSpeed, setVoiceSpeed] = useState(settings?.voice_speed || 1.0);

  const handleSave = async () => {
    await onSave({
      assistant_name: name,
      personality,
      voice,
      voice_enabled: voiceEnabled,
      voice_speed: voiceSpeed
    });
    onClose();
  };

  const voices = [
    { id: 'nova', name: 'Nova', description: 'Energetic, upbeat' },
    { id: 'alloy', name: 'Alloy', description: 'Neutral, balanced' },
    { id: 'echo', name: 'Echo', description: 'Smooth, calm' },
    { id: 'onyx', name: 'Onyx', description: 'Deep, authoritative' },
    { id: 'shimmer', name: 'Shimmer', description: 'Bright, cheerful' },
    { id: 'fable', name: 'Fable', description: 'Expressive, dramatic' },
    { id: 'coral', name: 'Coral', description: 'Warm, friendly' },
    { id: 'sage', name: 'Sage', description: 'Wise, measured' },
    { id: 'ash', name: 'Ash', description: 'Soft, gentle' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className={`w-full max-w-md rounded-2xl shadow-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
          <h2 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Assistant Settings</h2>
          <button onClick={onClose} className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Name */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Assistant Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              maxLength={20}
            />
          </div>

          {/* Personality */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Personality</label>
            <select
              value={personality}
              onChange={(e) => setPersonality(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
            >
              <option value="professional">Professional</option>
              <option value="friendly">Friendly</option>
            </select>
          </div>

          {/* Voice Enabled */}
          <div className="flex items-center justify-between">
            <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Voice Responses</label>
            <button
              onClick={() => setVoiceEnabled(!voiceEnabled)}
              className={`w-12 h-6 rounded-full transition-colors ${voiceEnabled ? 'bg-violet-600' : darkMode ? 'bg-gray-600' : 'bg-gray-300'}`}
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow transform transition-transform ${voiceEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
            </button>
          </div>

          {/* Voice Selection */}
          {voiceEnabled && (
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Voice</label>
              <select
                value={voice}
                onChange={(e) => setVoice(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
              >
                {voices.map((v) => (
                  <option key={v.id} value={v.id}>{v.name} - {v.description}</option>
                ))}
              </select>
            </div>
          )}

          {/* Voice Speed */}
          {voiceEnabled && (
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Voice Speed: {voiceSpeed.toFixed(1)}x</label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={voiceSpeed}
                onChange={(e) => setVoiceSpeed(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          )}

          {/* Live Activity Feed */}
          <div className={`pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Proactive Updates</h3>
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Live Activity Feed</span>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {liveFeedEnabled ? 'I\'ll proactively share important updates' : 'Only responds when asked'}
                </p>
              </div>
              <button
                onClick={() => setLiveFeedEnabled(!liveFeedEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  liveFeedEnabled ? 'bg-violet-600' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                }`}
                data-testid="live-feed-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    liveFeedEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Behavior Settings */}
          <div className={`pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Behavior</h3>
            <div className="flex items-center justify-between">
              <div>
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Reset position when closed</span>
                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {resetPositionOnClose ? 'Returns to default position' : 'Stays where you moved it'}
                </p>
              </div>
              <button
                onClick={() => setResetPositionOnClose(!resetPositionOnClose)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  resetPositionOnClose ? 'bg-violet-600' : darkMode ? 'bg-gray-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    resetPositionOnClose ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Clear History */}
          <div className={`pt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <button
              onClick={() => {
                if (window.confirm('Clear all conversation history?')) {
                  onClearHistory();
                }
              }}
              className="text-red-500 hover:text-red-600 text-sm font-medium"
            >
              Clear Conversation History
            </button>
          </div>
        </div>

        <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-end gap-2`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}
